/* eslint-disable operator-linebreak */
// Packages
import React, { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Button, Typography, Stepper, Step, StepButton } from '@mui/material';
import moment from 'moment';
import { omit, get, isNil, isEmpty } from 'lodash';
import { useNavigate, useParams, Link } from 'react-router-dom';
import PropTypes from 'prop-types';

// Relatives
import Layout from '../../layouts/Layout/Layout';
import http from '../../services/api/http';
import AppContext from '../../contexts/AppContext';
import UserContext from '../../contexts/UserContext';
import FundraiserStep1 from './createSteps/FundraiserStep1';
import FundraiserStep2 from './createSteps/FundraiserStep2';
import FundraiserStep3 from './createSteps/FundraiserStep3';
import FundraiserStep4 from './createSteps/FundraiserStep4';
import FundraiserStep5 from './createSteps/FundraiserStep5';

const FundraiserCreate = props => {
  const { updateMode } = props;
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [loading, setLoading] = useState(updateMode);
  const [stepLoading, setStepLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [cache, setCache] = useState({});
  const { apiHost } = useContext(AppContext);
  const { token, userNGOSelected } = useContext(UserContext);
  const navigate = useNavigate();

  const fundraiserForm = useForm({
    defaultValues: {
      // step 1
      name: '',
      message: '',
      // step 2
      country: '',
      street_address: '',
      street_address_2: '',
      city: '',
      state: '',
      zip: '',
      date_base: '',
      date_from: '',
      date_to: '',
      // step 3
      target_goal: '1000',
      // step 4
      media_id: '',
      banner_id: '',
      // step 5
      notes_list_ids: ''
    }
  });

  const steps = [
    {
      label: 'Fundraiser details',
      content: <FundraiserStep1 />
    },
    {
      label: 'Location',
      content: <FundraiserStep2 updateMode={updateMode} cache={cache} />
    },
    {
      label: 'Set your Fundraiser Goal',
      content: <FundraiserStep3 />
    },
    {
      label: 'Add a cover photo',
      content: (
        <FundraiserStep4
          setStepLoading={setStepLoading}
          cache={cache}
          setCache={setCache}
          setUploadLoading={setUploadLoading}
        />
      )
    },
    {
      label: 'Set your notes',
      content: <FundraiserStep5 cache={cache} />
    }
  ];

  const handleStep = step => async () => {
    if (await fundraiserForm.trigger()) {
      setActiveStep(step);
    }
  };

  const handleBack = () => setActiveStep(state => state - 1);

  const handleNext = async () => {
    if (await fundraiserForm.trigger()) {
      setActiveStep(state => state + 1);
    }
  };

  const stepperSubmit = data => {
    if (activeStep !== steps.length - 1) {
      setCompleted(state => ({ ...state, [activeStep]: true }));
      setActiveStep(activeStep + 1);
    } else {
      handleSubmit(data);
    }
  };

  // const handleDraft = async (/* formData */) => {};

  const handleSubmit = async formData => {
    if (!formData || !token) {
      return;
    }

    setLoading(true);
    const { date_base: dateBase, date_from: dateFrom, date_to: dateTo } = formData;

    const timeStart = dateFrom
      ? moment(dateBase)
          .set({ hour: dateFrom.getHours(), minute: dateFrom.getMinutes(), second: dateFrom.getSeconds() })
          .utc()
          .valueOf() / 1000
      : '';

    const timeEnd = dateTo
      ? moment(dateBase)
          .set({ hour: dateTo.getHours(), minute: dateTo.getMinutes(), second: dateTo.getSeconds() })
          .utc()
          .valueOf() / 1000
      : '';
    let response;
    let notesListIds = String(formData.notes_list_ids);

    if (!isNil(formData.notes_list_ids) && !isEmpty(formData.notes_list_ids) && formData.notes_list_ids.lenght > 1) {
      notesListIds = formData.notes_list_ids.join(',');
    }

    if (isNil(formData.notes_list_ids)) {
      notesListIds = String(0);
    }

    if (formData.address_manual_mode === true) {
      formData = {
        ...formData,
        lat: null,
        lon: null,
        notes_list_ids: notesListIds,
        skip_address_lookup: 1,
        skip_timezone_lookup: 1
      };
    } else {
      formData = {
        ...formData,
        notes_list_ids: notesListIds,
        skip_address_lookup: 1,
        skip_timezone_lookup: 1
      };
    }

    // Admin rules of events
    const userAdmins = get(userNGOSelected, '_misc.non_profit_ngo_members_except_me');
    if (!isNil(userAdmins) && !isEmpty(userAdmins)) {
      formData.additional_event_admins = userAdmins;
    }
    // Admin rules of events

    if (!updateMode) {
      response = await http(apiHost, token.accessToken, token.tokenType)
        .post('/api/web/v1/events/new-event', {
          ...omit(formData, ['date_from', 'date_to']),
          datesStartEnd: [`${timeStart}, ${timeEnd}`],
          artist_ids: [userNGOSelected.virtual_artist_id],
          event_type: 'association-fundraiser',
          status: 1 // 1 = publish, 4 = draft
        })
        .catch(() => {});
    } else if (updateMode && id) {
      response = await http(apiHost, token.accessToken, token.tokenType)
        .post('/api/web/v1/events/update-event', {
          ...omit(formData, ['date_from', 'date_to', 'full_address']),
          id,
          hour_date_start: timeStart,
          hour_date_end: timeEnd,
          artist_ids: [userNGOSelected.virtual_artist_id],
          event_type: 'association-fundraiser',
          status: 1 // 1 = publish, 4 = draft
        })
        .catch(() => {});
    } else {
      console.log('Something is wrong');
    }

    setLoading(false);
    if (!response || !response.data) {
      return;
    }

    const { success, error, data } = get(response, 'data', {});
    if (!success && error) {
      error.fieldErrors.forEach(({ field, message }) =>
        fundraiserForm.setError(field, { type: 'required', message }, { shouldFocus: true })
      );

      return;
    }

    if (!updateMode) {
      const responseSecondUpdate = await http(apiHost, token.accessToken, token.tokenType).post(
        '/api/web/v1/events/update-event',
        {
          ...omit(formData, ['date_from', 'date_to', 'full_address']),
          id: data.id,
          hour_date_start: timeStart,
          hour_date_end: timeEnd,
          artist_ids: [userNGOSelected.virtual_artist_id],
          event_type: 'association-fundraiser',
          status: 1 // 1 = publish, 4 = draft
        }
      );

      if (responseSecondUpdate) {
        navigate('/fundraisers');
      }
    } else {
      navigate('/fundraisers');
    }
  };

  const loadModel = async () => {
    const response = await http(apiHost, token.accessToken, token.tokenType)
      .get(`/api/web/v2/events/${id}`)
      .catch(() => {});

    const { success, output, error } = get(response, 'data', {});
    if (!success) {
      console.warn(error);
      setLoading(false);

      return;
    }

    const {
      name,
      message,
      country,
      city,
      state,
      zip,
      street_address: streetAddress,
      street_address_2: streetAddress2,
      formatted_address: fullAddress,
      target_goal: targetGoal,
      media_id: avatarId,
      banner_id: bannerId
    } = output;

    const fromDate = get(output, '_related.event_dates.0.start_date');
    const toDate = get(output, '_related.event_dates.0.end_date');
    const imageOriginal = get(output, '_misc.avatar.original_image');
    const bannerOriginal = get(output, '_misc.banner.original_image');
    const eventNotes = get(output, '_related.event_notes_lists');
    const lat = get(output, 'lat');
    const lon = get(output, 'lon');

    fundraiserForm.reset({
      // step 1
      name,
      message,
      // step 2
      formatted_address: fullAddress,
      country,
      street_address: streetAddress,
      street_address_2: streetAddress2,
      city,
      state,
      zip,
      date_base: fromDate ? new Date(moment(fromDate * 1000).format('MM/DD/YYYY')) : '',
      date_from: fromDate ? new Date(moment(fromDate * 1000)) : '',
      date_to: toDate ? new Date(moment(toDate * 1000)) : '',
      // step 3
      target_goal: String(targetGoal),
      // step 4
      media_id: avatarId,
      banner_id: bannerId
    });

    setCache({ media_id: imageOriginal, banner_id: bannerOriginal, event_notes: eventNotes, lat, lon });

    setLoading(false);
  };

  const handleCancel = () => {
    navigate('/fundraisers');
  };

  useEffect(() => {
    if (updateMode && id) {
      loadModel();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (uploadLoading) {
      setLoading(true);
    }

    if (!uploadLoading) {
      setLoading(false);
    }
  }, [uploadLoading]);

  return (
    <Layout
      title="Create Fundraiser"
      breadcrumb={['Home', <Link to="/fundraisers">Fundraisers</Link>, 'Create Fundraiser']}
    >
      <div className="fundraiser-create">
        <div className="d-flex flex-column col-sm-8 align-items-center justify-content-center mx-auto">
          {activeStep > 0 && (
            <Typography color="primary" variant="h5" className="fundraiser-title-name text-right mx-auto">
              {`Fundariser: ${fundraiserForm.getValues('name')}`}
            </Typography>
          )}
          <Stepper
            nonLinear
            activeStep={activeStep}
            className={`py-4 align-self-stretch ${uploadLoading ? 'v-hidden' : 'show'}`}
          >
            {steps.map(({ label }, index) => (
              <Step key={label} completed={completed[index]} className="hide-sm-label">
                <StepButton color="inherit" onClick={handleStep(index)}>
                  {/* {label} */}
                </StepButton>
              </Step>
            ))}
          </Stepper>
          <FormProvider {...fundraiserForm}>
            <Box
              component="form"
              onSubmit={fundraiserForm.handleSubmit(stepperSubmit)}
              className="align-self-stretch"
              autoComplete="off"
            >
              <div className="d-flex flex-column align-items-stretch">
                <Typography color="primary" variant="h1" className="text-center mx-auto">
                  {steps[activeStep].label}
                </Typography>
                {steps[activeStep].content}
              </div>
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Button
                  color="primary"
                  variant="outlined"
                  className="btn-forms"
                  disabled={activeStep === 0 || uploadLoading}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                {activeStep + 1 === steps.length ? (
                  <Button type="submit" variant="outlined" className="btn-forms" disabled={loading || stepLoading}>
                    {updateMode ? 'Update and Publish' : 'Save and Publish'}
                  </Button>
                ) : (
                  <>
                    <Button
                      sx={{ mr: 1 }}
                      color="primary"
                      type="button"
                      variant="outlined"
                      className="btn-forms"
                      disabled={loading || stepLoading}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    {/* <Button
                      sx={{ mr: 1 }}
                      color="primary"
                      type="button"
                      variant="outlined"
                      className="btn-forms"
                      disabled={loading || stepLoading}
                      onClick={handleDraft}
                    >
                      Save as Draft
                    </Button> */}

                    <Button
                      id="nextBtn"
                      sx={{ mr: 1 }}
                      color="primary"
                      type="button"
                      variant="outlined"
                      className="btn-forms"
                      disabled={loading || stepLoading}
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </FormProvider>
        </div>
      </div>
    </Layout>
  );
};

FundraiserCreate.defaultProps = {
  updateMode: false
};

FundraiserCreate.propTypes = {
  updateMode: PropTypes.bool
};

export default FundraiserCreate;
